import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Plus, Pencil, Trash2, Shield, Eye, SlidersHorizontal, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const ROLE_LABELS = {
  view: { label: 'Solo Visualizzazione', icon: Eye, color: 'bg-slate-500' },
  admin: { label: 'Admin', icon: Shield, color: 'bg-emerald-500' },
  custom: { label: 'Permessi Personalizzati', icon: SlidersHorizontal, color: 'bg-blue-500' }
};

const ROLE_DESCRIPTIONS = {
  view: 'Può solo visualizzare tutte le sezioni.',
  admin: 'Accesso completo: crea, modifica, elimina, gestisce utenti.',
  custom: 'Seleziona i permessi atomici da assegnare.'
};

const ATOMIC_PERMISSIONS = [
  { key: 'macro_project:create', label: 'Crea Macro Progetto' },
  { key: 'project:create', label: 'Crea Progetti' },
  { key: 'task:create', label: 'Crea Task' },
  { key: 'workload:view', label: 'Carichi' },
  { key: 'project_analysis:view', label: 'Analisi Progetti' },
  { key: 'dashboard:view', label: 'Dashboard' },
  { key: 'ai_insight:use', label: 'AI Insight' },
  { key: 'import:use', label: 'Import' },
  { key: 'export:use', label: 'Export' },
  { key: 'journal:view', label: 'Journal' },
  { key: 'ai_agent:mutate', label: 'Agente AI (modifiche)' }
];

const ATOMIC_LABELS = ATOMIC_PERMISSIONS.reduce(function(acc, perm) {
  acc[perm.key] = perm.label;
  return acc;
}, {});

const LEGACY_EXPORT_PERMS = new Set(ATOMIC_PERMISSIONS.map(function(p) { return p.key; }));

const normalizePermissionsValue = function(value) {
  if (value === 'admin' || value === 'view') {
    return { role: value, perms: [] };
  }
  if (value === 'export') {
    return { role: 'custom', perms: Array.from(LEGACY_EXPORT_PERMS) };
  }
  if (Array.isArray(value)) {
    return { role: 'custom', perms: value };
  }
  return { role: 'view', perms: [] };
};

export const UsersTab = ({ authAxios, apiUrl }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: 'view',
    permissions: [],
    journalEnabled: false
  });

  // Load users
  const loadUsers = async function() {
    try {
      setLoading(true);
      const response = await authAxios.get(apiUrl + '/users');
      const normalizedUsers = response.data.map(function(user) {
        return { ...user, journalEnabled: Boolean(user.journalEnabled) };
      });
      setUsers(normalizedUsers);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Errore nel caricamento degli utenti');
    } finally {
      setLoading(false);
    }
  };

  useEffect(function() {
    loadUsers();
  }, []);

  const handleOpenDialog = function(user) {
    if (user) {
      const normalized = normalizePermissionsValue(user.permissions);
      setSelectedUser(user);
      setFormData({
        username: user.username,
        password: '',
        role: normalized.role,
        permissions: normalized.perms,
        journalEnabled: Boolean(user.journalEnabled)
      });
    } else {
      setSelectedUser(null);
      setFormData({
        username: '',
        password: '',
        role: 'view',
        permissions: [],
        journalEnabled: false
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async function(e) {
    e.preventDefault();
    if (!formData.username) return;
    if (!selectedUser && !formData.password) {
      toast.error('La password è obbligatoria per i nuovi utenti');
      return;
    }

    try {
      if (selectedUser) {
        // Update
        const updateData = {
          username: formData.username,
          permissions: formData.role === 'custom' ? formData.permissions : formData.role,
          journalEnabled: formData.journalEnabled
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await authAxios.put(apiUrl + '/users/' + selectedUser.id, updateData);
        setUsers(function(prev) {
          return prev.map(function(u) {
            if (u.id === selectedUser.id) {
              return {
                ...u,
                username: formData.username,
                permissions: formData.role === 'custom' ? formData.permissions : formData.role,
                journalEnabled: formData.journalEnabled
              };
            }
            return u;
          });
        });
        toast.success('Utente aggiornato');
      } else {
        // Create
        const response = await authAxios.post(apiUrl + '/users', {
          username: formData.username,
          password: formData.password,
          permissions: formData.role === 'custom' ? formData.permissions : formData.role,
          journalEnabled: formData.journalEnabled
        });
        setUsers(function(prev) { return [...prev, response.data]; });
        toast.success('Utente creato');
      }
      setDialogOpen(false);
    } catch (error) {
      console.error('Error saving user:', error);
      if (error.response && error.response.data && error.response.data.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Errore nel salvataggio');
      }
    }
  };

  const handleDelete = async function() {
    if (!selectedUser) return;
    
    try {
      await authAxios.delete(apiUrl + '/users/' + selectedUser.id);
      setUsers(function(prev) {
        return prev.filter(function(u) { return u.id !== selectedUser.id; });
      });
      toast.success('Utente eliminato');
      setDeleteDialogOpen(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Error deleting user:', error);
      if (error.response && error.response.data && error.response.data.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Errore nell\'eliminazione');
      }
    }
  };

  const openDeleteDialog = function(user) {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Gestione Utenti</h2>
          <p className="text-sm text-muted-foreground">Crea e gestisci gli utenti dell'applicazione</p>
        </div>
        <Button onClick={function() { handleOpenDialog(null); }} data-testid="add-user-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nuovo Utente
        </Button>
      </div>

      <div className="border border-border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Username</TableHead>
              <TableHead>Permessi</TableHead>
              <TableHead>Journal</TableHead>
              <TableHead className="w-[100px]">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map(function(user) {
              const normalized = normalizePermissionsValue(user.permissions);
              const roleInfo = ROLE_LABELS[normalized.role] || ROLE_LABELS.view;
              const IconComponent = roleInfo.icon;
              const permsText = normalized.role === 'custom'
                ? normalized.perms.map(function(p) { return ATOMIC_LABELS[p] || p; }).join(', ')
                : '';
              
              return (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">{user.username}</TableCell>
                  <TableCell>
                    <Badge className={cn("gap-1", roleInfo.color)}>
                      <IconComponent className="w-3 h-3" />
                      {roleInfo.label}
                    </Badge>
                    {normalized.role === 'custom' && permsText && (
                      <div className="text-xs text-muted-foreground mt-1">{permsText}</div>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className={cn(
                      "text-xs font-medium",
                      user.journalEnabled ? "text-emerald-600" : "text-muted-foreground"
                    )}>
                      {user.journalEnabled ? "Attivo" : "Disattivo"}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={function() { handleOpenDialog(user); }}
                        data-testid={'edit-user-' + user.id}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={function() { openDeleteDialog(user); }}
                        data-testid={'delete-user-' + user.id}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {users.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                  Nessun utente trovato
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* User Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[480px] max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {selectedUser ? 'Modifica Utente' : 'Nuovo Utente'}
            </DialogTitle>
            <DialogDescription>
              {selectedUser ? 'Modifica i dettagli dell\'utente' : 'Crea un nuovo utente'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSave} className="flex flex-col gap-4 mt-4 min-h-0">
            <div className="space-y-4 overflow-y-auto pr-1">
              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  value={formData.username}
                  onChange={function(e) { setFormData(function(prev) { return { ...prev, username: e.target.value }; }); }}
                  placeholder="Es: mario.rossi"
                  data-testid="user-username-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">
                  Password {selectedUser && <span className="text-muted-foreground">(lascia vuoto per non modificare)</span>}
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={function(e) { setFormData(function(prev) { return { ...prev, password: e.target.value }; }); }}
                  placeholder={selectedUser ? '••••••••' : 'Inserisci password'}
                  data-testid="user-password-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Permessi</Label>
                <div className="space-y-2">
                  {Object.entries(ROLE_LABELS).map(function(entry) {
                    const key = entry[0];
                    const info = entry[1];
                    const IconComponent = info.icon;
                    const isSelected = formData.role === key;

                    return (
                      <div
                        key={key}
                        className={cn(
                          "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
                          isSelected ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50"
                        )}
                        onClick={function() { setFormData(function(prev) { return { ...prev, role: key }; }); }}
                        data-testid={'permission-' + key}
                      >
                        <div className={cn("p-2 rounded-full", info.color)}>
                          <IconComponent className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{info.label}</p>
                          <p className="text-xs text-muted-foreground">{ROLE_DESCRIPTIONS[key]}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {formData.role === 'custom' && (
                  <div className="mt-3 space-y-2 rounded-lg border border-border p-3">
                    <p className="text-xs text-muted-foreground">Permessi atomici</p>
                    <div className="space-y-2">
                      {ATOMIC_PERMISSIONS.map(function(permission) {
                        const checked = formData.permissions.includes(permission.key);
                        return (
                          <label
                            key={permission.key}
                            className="flex items-center gap-2 text-sm cursor-pointer"
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={function(next) {
                                setFormData(function(prev) {
                                  const hasPerm = prev.permissions.includes(permission.key);
                                  const nextPerms = next === true || (next && !hasPerm)
                                    ? Array.from(new Set([...prev.permissions, permission.key]))
                                    : prev.permissions.filter(function(p) { return p !== permission.key; });
                                  return { ...prev, permissions: nextPerms };
                                });
                              }}
                            />
                            <span>{permission.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Journal</Label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={formData.journalEnabled}
                    onCheckedChange={function(next) {
                      setFormData(function(prev) {
                        return { ...prev, journalEnabled: next === true };
                      });
                    }}
                    data-testid="user-journal-enabled"
                  />
                  <span>Abilita log per questo utente</span>
                </label>
                <p className="text-xs text-muted-foreground">
                  Se disattivo, le azioni dell’utente non vengono registrate nel journal.
                </p>
              </div>
            </div>

            <DialogFooter className="mt-2">
              <Button type="button" variant="outline" onClick={function() { setDialogOpen(false); }}>
                Annulla
              </Button>
              <Button
                type="submit"
                disabled={!formData.username || (!selectedUser && !formData.password)}
                data-testid="save-user-btn"
              >
                {selectedUser ? 'Salva Modifiche' : 'Crea Utente'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Conferma Eliminazione</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare l'utente <strong>{selectedUser?.username}</strong>? 
              Questa azione non può essere annullata.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={function() { setDeleteDialogOpen(false); }}>
              Annulla
            </Button>
            <Button type="button" variant="destructive" onClick={handleDelete} data-testid="confirm-delete-user">
              Elimina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersTab;
